import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getInstitutionRequests, approveInstitutionRequest, denyInstitutionRequest } from '../../utils/remote';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useState } from 'react';
import { Modal, Button } from 'flowbite-react';

const StudentRequests = () => {
  const { token } = useAuth() as any;
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'deny' | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['institutionRequests'],
    queryFn: () => getInstitutionRequests(token),
    enabled: !!token,
  });

  const openConfirm = (action: 'approve' | 'deny', req: any) => {
    setConfirmAction(action);
    setSelectedRequest(req);
    setConfirmOpen(true);
  };

  const runConfirmedAction = async () => {
    if (!confirmAction || !selectedRequest) return;
    try {
      if (confirmAction === 'approve') {
        await approveInstitutionRequest(token, selectedRequest.id);
      } else {
        await denyInstitutionRequest(token, selectedRequest.id);
      }
      qc.invalidateQueries({ queryKey: ['institutionRequests'] });
      setConfirmOpen(false);
      setSelectedRequest(null);
      setConfirmAction(null);
    } catch (e) {
      console.error(`${confirmAction} failed`, e);
      // keep modal open so user can retry or cancel
      alert(`${confirmAction} failed`);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Student Requests" />

      <div className="flex flex-col gap-4">
        <div className="rounded-md bg-white p-6 shadow">
          <h2 className="text-lg font-semibold">Student Requests</h2>
          {isLoading && <p className="mt-2 text-sm text-gray-600">Loading...</p>}
          {isError && <p className="mt-2 text-sm text-red-600">Error loading requests</p>}
          {!isLoading && data?.requests && data.requests.length === 0 && (
            <p className="mt-2 text-sm text-gray-600">No requests at the moment.</p>
          )}

          {!isLoading && data?.requests && data.requests.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="text-left text-sm text-gray-600">
                    <th className="px-3 py-2">Full name</th>
                    <th className="px-3 py-2">Grade</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Requested at</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.requests.map((r: any) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">{r.full_name || '-'}</td>
                      <td className="px-3 py-2">{r.grade_name || '-'}</td>
                      <td className="px-3 py-2">{r.email || '-'}</td>
                      <td className="px-3 py-2">{new Date(r.created).toLocaleString()}</td>
                      <td className="px-3 py-2">{r.status}</td>
                      <td className="px-3 py-2">
                        {r.status === 'pending' ? (
                          <div className="flex gap-2">
                            <button onClick={() => openConfirm('approve', r)} className="rounded bg-eltest-blue px-3 py-1 text-white">Approve</button>
                            <button onClick={() => openConfirm('deny', r)} className="rounded bg-red-600 px-3 py-1 text-white">Deny</button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">{r.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Modal.Header>{confirmAction === 'approve' ? 'Confirm Approve' : 'Confirm Deny'}</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p>Are you sure you want to {confirmAction} the request for <strong>{selectedRequest?.full_name || '-'}</strong>?</p>
            {selectedRequest?.grade_name && <p className="text-sm text-gray-600">Grade: {selectedRequest.grade_name}</p>}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex gap-2">
            <Button color="gray" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color={confirmAction === 'approve' ? 'success' : 'failure'} onClick={runConfirmedAction}>{confirmAction === 'approve' ? 'Approve' : 'Deny'}</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StudentRequests;
